import { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { View } from "react-native";

import { ProtectedScreen } from "~/components/ui";
import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppText,
  Button,
  ButtonText,
  Card,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import {
  CADENCE_OPTIONS,
  INVOICE_STATUS_ACTIONS,
  RecurringLineItemEditor,
  createRecurringLineItem,
  dateOnly,
  formatDuration,
  formatMoney,
  hasRecurringLineItem,
  lineGst,
  lineSubtotal,
  recurringTemplateLineItems,
  serializeRecurringLineItems,
  makeStylesheet,
  timeEntryValue,
  todayPlus,
  type RecurringLineItemForm,
} from "~/features/invoices";
import { useThemedStyles } from "~/styles/theme";
import { timeRoundingLabel } from "~/utils/time-rounding";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceInvoiceLifecycle,
  useWorkspaceInvoiceReminders,
  useWorkspaceInvoiceItems,
  useWorkspaceInvoices,
  useWorkspaceMembership,
  useWorkspaceProjects,
  useWorkspaceRecurringInvoices,
  useWorkspaceRebillableExpenses,
  useWorkspaceTasks,
  useWorkspaceTimeEntries,
} from "~/hooks/workspace";
import { useAuth } from "~/hooks/useAuth";
import { useWorkspaceSubscription } from "~/hooks/useSubscription";

export default function InvoicesScreen() {
  const stylesheet = useThemedStyles(makeStylesheet);
  const router = useRouter();
  const { user } = useAuth();
  const workspace = usePrimaryWorkspace();
  const subscription = useWorkspaceSubscription(workspace.data?.id);
  const workspaceMembership = useWorkspaceMembership(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const invoices = useWorkspaceInvoices(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);
  const records = invoices.data ?? [];
  const clientRecords = clients.data ?? [];
  const projectRecords = projects.data ?? [];
  const taskRecords = tasks.data ?? [];
  const clientById = new Map(
    clientRecords.map((client) => [client.id, client]),
  );
  const projectById = new Map(
    projectRecords.map((project) => [project.id, project]),
  );
  const taskById = new Map(taskRecords.map((task) => [task.id, task]));
  const clientOptions = [
    { label: "No client", value: "" },
    ...clientRecords.map((client) => ({
      label: client.name,
      value: client.id,
    })),
  ];
  const invoiceIds = useMemo(
    () => records.map((invoice) => invoice.id),
    [records],
  );
  const invoiceLifecycle = useWorkspaceInvoiceLifecycle(
    workspace.data?.id,
    invoiceIds,
  );
  const invoiceItems = useWorkspaceInvoiceItems(workspace.data?.id, invoiceIds);
  const invoiceReminders = useWorkspaceInvoiceReminders(
    workspace.data?.id,
    invoiceIds,
  );
  const recurringInvoices = useWorkspaceRecurringInvoices(workspace.data?.id);
  const rebillableExpenses = useWorkspaceRebillableExpenses(workspace.data?.id);
  const canManageWorkspace = Boolean(
    workspace.data?.owner_id === user?.id ||
    workspaceMembership.data?.role === "admin" ||
    workspaceMembership.data?.role === "owner",
  );
  const canUseAutomatedReminders =
    subscription.canUseAutomatedReminders && canManageWorkspace;
  const canUseRecurringInvoices =
    subscription.canUseRecurringInvoices && canManageWorkspace;
  const [clientId, setClientId] = useState("");
  const [dueDate, setDueDate] = useState(todayPlus(14));
  const [notes, setNotes] = useState("");
  const [purchaseOrderReference, setPurchaseOrderReference] = useState("");
  const [clientReference, setClientReference] = useState("");
  const [jobStartsOn, setJobStartsOn] = useState("");
  const [jobEndsOn, setJobEndsOn] = useState("");
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null,
  );
  const [invoiceEditOpen, setInvoiceEditOpen] = useState(false);
  const [editInvoiceClientId, setEditInvoiceClientId] = useState("");
  const [editInvoiceDueDate, setEditInvoiceDueDate] = useState("");
  const [editInvoiceNotes, setEditInvoiceNotes] = useState("");
  const [
    editInvoicePurchaseOrderReference,
    setEditInvoicePurchaseOrderReference,
  ] = useState("");
  const [editInvoiceClientReference, setEditInvoiceClientReference] =
    useState("");
  const [editInvoiceJobStartsOn, setEditInvoiceJobStartsOn] = useState("");
  const [editInvoiceJobEndsOn, setEditInvoiceJobEndsOn] = useState("");
  const [editInvoiceAmendmentReason, setEditInvoiceAmendmentReason] =
    useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitAmount, setUnitAmount] = useState("");
  const [gstRate, setGstRate] = useState(
    workspace.data?.gst_registered ? "15" : "0",
  );
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editItemDescription, setEditItemDescription] = useState("");
  const [editItemQuantity, setEditItemQuantity] = useState("1");
  const [editItemUnitAmount, setEditItemUnitAmount] = useState("");
  const [editItemGstRate, setEditItemGstRate] = useState("0");
  const [timeEntryGstRate, setTimeEntryGstRate] = useState(
    workspace.data?.gst_registered ? "15" : "0",
  );
  const [reminderDate, setReminderDate] = useState(todayPlus(7));
  const [editingReminderId, setEditingReminderId] = useState<string | null>(
    null,
  );
  const [editReminderDate, setEditReminderDate] = useState("");
  const [reminderDeliveryResult, setReminderDeliveryResult] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayPlus(0));
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [recurringClientId, setRecurringClientId] = useState("");
  const [recurringTitle, setRecurringTitle] = useState("");
  const [recurringCadence, setRecurringCadence] = useState("monthly");
  const [recurringNextIssueDate, setRecurringNextIssueDate] = useState(
    todayPlus(30),
  );
  const [recurringDueDays, setRecurringDueDays] = useState("14");
  const [recurringEndsOn, setRecurringEndsOn] = useState("");
  const [recurringMaxOccurrences, setRecurringMaxOccurrences] = useState("");
  const [recurringLineItems, setRecurringLineItems] = useState<
    RecurringLineItemForm[]
  >([
    createRecurringLineItem({
      gstRate: workspace.data?.gst_registered ? "15" : "0",
    }),
  ]);
  const [recurringNotes, setRecurringNotes] = useState("");
  const [recurringPaymentInstructions, setRecurringPaymentInstructions] =
    useState("");
  const [recurringGenerationResult, setRecurringGenerationResult] =
    useState("");
  const [editingRecurringId, setEditingRecurringId] = useState<string | null>(
    null,
  );
  const [editRecurringClientId, setEditRecurringClientId] = useState("");
  const [editRecurringTitle, setEditRecurringTitle] = useState("");
  const [editRecurringCadence, setEditRecurringCadence] = useState("monthly");
  const [editRecurringNextIssueDate, setEditRecurringNextIssueDate] =
    useState("");
  const [editRecurringDueDays, setEditRecurringDueDays] = useState("14");
  const [editRecurringEndsOn, setEditRecurringEndsOn] = useState("");
  const [editRecurringMaxOccurrences, setEditRecurringMaxOccurrences] =
    useState("");
  const [editRecurringLineItems, setEditRecurringLineItems] = useState<
    RecurringLineItemForm[]
  >([createRecurringLineItem()]);
  const [editRecurringNotes, setEditRecurringNotes] = useState("");
  const [
    editRecurringPaymentInstructions,
    setEditRecurringPaymentInstructions,
  ] = useState("");
  const filteredInvoices = records.filter((invoice) => {
    const clientName = invoice.client_id
      ? (clientById.get(invoice.client_id)?.name ?? "")
      : "";
    const matchesSearch = `${invoice.invoice_number} ${clientName}`
      .toLowerCase()
      .includes(invoiceSearch.trim().toLowerCase());
    return (
      matchesSearch &&
      (!invoiceStatusFilter || invoice.status === invoiceStatusFilter)
    );
  });
  const selectedInvoice =
    records.find((invoice) => invoice.id === selectedInvoiceId) ??
    records.find((invoice) => invoice.status === "draft") ??
    records[0] ??
    null;
  const selectedClient = selectedInvoice?.client_id
    ? clientById.get(selectedInvoice.client_id)
    : null;
  const selectedItems = selectedInvoice
    ? invoiceItems.items.filter(
        (item) => item.invoice_id === selectedInvoice.id,
      )
    : [];
  const selectedRefunds = selectedInvoice
    ? invoiceLifecycle.refunds.filter(
        (refund) => refund.invoice_id === selectedInvoice.id,
      )
    : [];
  const selectedPayments = selectedInvoice
    ? invoiceLifecycle.payments.filter(
        (payment) => payment.invoice_id === selectedInvoice.id,
      )
    : [];
  const selectedAmendments = selectedInvoice
    ? invoiceLifecycle.amendments.filter(
        (amendment) => amendment.invoice_id === selectedInvoice.id,
      )
    : [];
  const paidTotal = selectedPayments.reduce(
    (total, payment) => total + payment.amount_cents,
    0,
  );
  const refundedTotal = selectedRefunds.reduce(
    (total, refund) => total + refund.amount_cents,
    0,
  );
  const remainingBalance = selectedInvoice
    ? Math.max(0, selectedInvoice.total_cents - paidTotal)
    : 0;
  const remainingRefundable = selectedInvoice
    ? Math.max(0, selectedInvoice.total_cents - refundedTotal)
    : 0;
  const allUnbilledTimeEntries = (timeEntries.data ?? []).filter(
    (entry) =>
      entry.billable &&
      !entry.invoice_id &&
      !entry.invoiced_at &&
      entry.duration_seconds > 0,
  );
  const entryClientId = (entry: (typeof allUnbilledTimeEntries)[number]) =>
    entry.client_id ??
    (entry.project_id ? projectById.get(entry.project_id)?.client_id : null) ??
    null;
  const invoiceReadyTimeEntries = !selectedInvoice?.client_id
    ? allUnbilledTimeEntries
    : allUnbilledTimeEntries.filter(
        (entry) => entryClientId(entry) === selectedInvoice.client_id,
      );
  const invoiceReadyExpenses = !selectedInvoice?.client_id
    ? rebillableExpenses.expenses
    : rebillableExpenses.expenses.filter(
        (expense) => expense.client_id === selectedInvoice.client_id,
      );
  const unbilledTimeByClient = allUnbilledTimeEntries.reduce<
    Map<string, { count: number; seconds: number; value: number }>
  >((totals, entry) => {
    const matchingClientId = entryClientId(entry);
    if (!matchingClientId) return totals;
    const current = totals.get(matchingClientId) ?? {
      count: 0,
      seconds: 0,
      value: 0,
    };
    totals.set(matchingClientId, {
      count: current.count + 1,
      seconds: current.seconds + entry.duration_seconds,
      value: current.value + timeEntryValue(entry),
    });
    return totals;
  }, new Map());
  const timeClientFocus = [...unbilledTimeByClient.entries()]
    .sort((left, right) => right[1].value - left[1].value)
    .slice(0, 4);
  const otherClientTimeEntryCount =
    allUnbilledTimeEntries.length - invoiceReadyTimeEntries.length;
  const selectedReminders = selectedInvoice
    ? invoiceReminders.reminders.filter(
        (reminder) => reminder.invoice_id === selectedInvoice.id,
      )
    : [];
  const outstanding = records
    .filter((invoice) => !invoice.paid_at && invoice.status !== "void")
    .reduce((total, invoice) => total + invoice.total_cents, 0);
  const error =
    workspace.error ??
    clients.error ??
    projects.error ??
    tasks.error ??
    invoices.error ??
    invoices.create.error ??
    invoices.sendEmail.error ??
    invoices.update.error ??
    invoices.updateStatus.error ??
    invoiceLifecycle.error ??
    invoiceLifecycle.createPayment.error ??
    invoiceLifecycle.createRefund.error ??
    invoiceItems.error ??
    invoiceItems.createManualItem.error ??
    invoiceItems.createFromTimeEntry.error ??
    invoiceItems.createFromExpense.error ??
    invoiceItems.deleteManualItem.error ??
    invoiceItems.updateManualItem.error ??
    invoiceReminders.error ??
    invoiceReminders.create.error ??
    invoiceReminders.cancel.error ??
    invoiceReminders.sendDue.error ??
    rebillableExpenses.error ??
    invoiceReminders.update.error ??
    recurringInvoices.error ??
    recurringInvoices.create.error ??
    recurringInvoices.generateDue.error ??
    recurringInvoices.setEnabled.error ??
    recurringInvoices.update.error;
  const errorMessage = error instanceof Error ? error.message : "";
  const canAddItem = Boolean(selectedInvoice && description.trim());

  async function createInvoice() {
    const created = await invoices.create.mutateAsync({
      clientId,
      dueDate,
      notes,
      purchaseOrderReference,
      clientReference,
      jobStartsOn,
      jobEndsOn,
    });
    setSelectedInvoiceId(created.id);
    setClientId("");
    setNotes("");
    setPurchaseOrderReference("");
    setClientReference("");
    setJobStartsOn("");
    setJobEndsOn("");
    setDueDate(todayPlus(14));
  }

  async function createInvoiceForTimeClient(targetClientId: string) {
    const created = await invoices.create.mutateAsync({
      clientId: targetClientId,
      dueDate,
      notes,
      purchaseOrderReference,
      clientReference,
      jobStartsOn,
      jobEndsOn,
    });
    setSelectedInvoiceId(created.id);
  }

  function startInvoiceEdit() {
    if (!selectedInvoice) return;
    setEditInvoiceClientId(selectedInvoice.client_id ?? "");
    setEditInvoiceDueDate(selectedInvoice.due_date ?? todayPlus(14));
    setEditInvoiceNotes(selectedInvoice.notes ?? "");
    setEditInvoicePurchaseOrderReference(
      selectedInvoice.purchase_order_reference ?? "",
    );
    setEditInvoiceClientReference(selectedInvoice.client_reference ?? "");
    setEditInvoiceJobStartsOn(selectedInvoice.job_starts_on ?? "");
    setEditInvoiceJobEndsOn(selectedInvoice.job_ends_on ?? "");
    setEditInvoiceAmendmentReason("");
    setInvoiceEditOpen(true);
  }

  async function saveInvoiceDetails() {
    if (!selectedInvoice) return;
    await invoices.update.mutateAsync({
      id: selectedInvoice.id,
      clientId: editInvoiceClientId,
      dueDate: editInvoiceDueDate,
      notes: editInvoiceNotes,
      purchaseOrderReference: editInvoicePurchaseOrderReference,
      clientReference: editInvoiceClientReference,
      jobStartsOn: editInvoiceJobStartsOn,
      jobEndsOn: editInvoiceJobEndsOn,
      amendmentReason: editInvoiceAmendmentReason,
    });
    setInvoiceEditOpen(false);
    setEditInvoiceAmendmentReason("");
  }

  async function addLineItem() {
    if (!selectedInvoice || !canAddItem) return;
    await invoiceItems.createManualItem.mutateAsync({
      invoiceId: selectedInvoice.id,
      description,
      quantity,
      unitAmount,
      gstRate,
    });
    setDescription("");
    setQuantity("1");
    setUnitAmount("");
  }

  function startLineItemEdit(item: (typeof selectedItems)[number]) {
    if (item.source !== "manual" || item.expense_id) return;
    setEditingItemId(item.id);
    setEditItemDescription(item.description);
    setEditItemQuantity(String(item.quantity));
    setEditItemUnitAmount(String(item.unit_amount_cents / 100));
    setEditItemGstRate(String(item.gst_rate));
  }

  async function saveLineItem(item: (typeof selectedItems)[number]) {
    if (!selectedInvoice) return;
    await invoiceItems.updateManualItem.mutateAsync({
      id: item.id,
      invoiceId: selectedInvoice.id,
      description: editItemDescription,
      quantity: editItemQuantity,
      unitAmount: editItemUnitAmount,
      gstRate: editItemGstRate,
    });
    setEditingItemId(null);
  }

  async function addTimeEntry(entry: (typeof allUnbilledTimeEntries)[number]) {
    if (!selectedInvoice) return;
    await invoiceItems.createFromTimeEntry.mutateAsync({
      invoiceId: selectedInvoice.id,
      timeEntry: entry,
      gstRate: timeEntryGstRate,
    });
  }

  async function addExpense(
    expense: (typeof rebillableExpenses.expenses)[number],
  ) {
    if (!selectedInvoice) return;
    await invoiceItems.createFromExpense.mutateAsync({
      expense,
      gstRate: timeEntryGstRate,
      invoiceId: selectedInvoice.id,
    });
  }

  async function scheduleReminder() {
    if (!selectedInvoice || !canUseAutomatedReminders) return;
    await invoiceReminders.create.mutateAsync({
      invoiceId: selectedInvoice.id,
      scheduledFor: reminderDate,
    });
    setReminderDate(todayPlus(7));
  }

  function startReminderEdit(reminder: (typeof selectedReminders)[number]) {
    if (reminder.status !== "scheduled") return;
    setEditingReminderId(reminder.id);
    setEditReminderDate(dateOnly(reminder.scheduled_for));
  }

  async function saveReminder(reminder: (typeof selectedReminders)[number]) {
    if (!canUseAutomatedReminders) return;
    await invoiceReminders.update.mutateAsync({
      id: reminder.id,
      scheduledFor: editReminderDate,
    });
    setEditingReminderId(null);
  }

  function addRecurringLine(
    target: "create" | "edit",
    seed?: Partial<RecurringLineItemForm>,
  ) {
    if (target === "create") {
      setRecurringLineItems((current) => [
        ...current,
        createRecurringLineItem(seed),
      ]);
      return;
    }
    setEditRecurringLineItems((current) => [
      ...current,
      createRecurringLineItem(seed),
    ]);
  }

  function updateRecurringLine(
    target: "create" | "edit",
    index: number,
    patch: Partial<RecurringLineItemForm>,
  ) {
    const updater = (current: RecurringLineItemForm[]) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      );
    if (target === "create") {
      setRecurringLineItems(updater);
      return;
    }
    setEditRecurringLineItems(updater);
  }

  function removeRecurringLine(target: "create" | "edit", index: number) {
    const updater = (current: RecurringLineItemForm[]) => {
      if (current.length <= 1) {
        return [createRecurringLineItem()];
      }
      return current.filter((_, itemIndex) => itemIndex !== index);
    };
    if (target === "create") {
      setRecurringLineItems(updater);
      return;
    }
    setEditRecurringLineItems(updater);
  }

  async function createRefund() {
    if (!selectedInvoice) return;
    await invoiceLifecycle.createRefund.mutateAsync({
      invoice: selectedInvoice,
      amount: refundAmount,
      reason: refundReason,
    });
    setRefundAmount("");
    setRefundReason("");
  }

  async function createPayment() {
    if (!selectedInvoice) return;
    await invoiceLifecycle.createPayment.mutateAsync({
      amount: paymentAmount,
      invoice: selectedInvoice,
      note: paymentNote,
      paidAt: paymentDate,
      reference: paymentReference,
    });
    setPaymentAmount("");
    setPaymentReference("");
    setPaymentNote("");
    setPaymentDate(todayPlus(0));
  }

  async function sendDueReminders() {
    if (!canUseAutomatedReminders) return;
    const result = await invoiceReminders.sendDue.mutateAsync({
      invoiceId: selectedInvoice?.id,
    });
    setReminderDeliveryResult(
      result
        ? `${result.sent} sent, ${result.failed} failed, ${result.skipped} skipped.`
        : "No due reminders were processed.",
    );
  }

  async function createRecurringInvoice() {
    if (!canUseRecurringInvoices) return;
    await recurringInvoices.create.mutateAsync({
      cadence: recurringCadence,
      clientId: recurringClientId,
      dueDays: recurringDueDays,
      endsOn: recurringEndsOn,
      lineItems: serializeRecurringLineItems(recurringLineItems),
      nextIssueDate: recurringNextIssueDate,
      maxOccurrences: recurringMaxOccurrences,
      notes: recurringNotes,
      paymentInstructions: recurringPaymentInstructions,
      title: recurringTitle,
    });
    setRecurringClientId("");
    setRecurringTitle("");
    setRecurringCadence("monthly");
    setRecurringNextIssueDate(todayPlus(30));
    setRecurringDueDays("14");
    setRecurringEndsOn("");
    setRecurringMaxOccurrences("");
    setRecurringLineItems([
      createRecurringLineItem({
        gstRate: workspace.data?.gst_registered ? "15" : "0",
      }),
    ]);
    setRecurringNotes("");
    setRecurringPaymentInstructions("");
  }

  function startRecurringEdit(
    template: NonNullable<typeof recurringInvoices.data>[number],
  ) {
    const items = recurringTemplateLineItems(template.template);
    setEditingRecurringId(template.id);
    setEditRecurringClientId(template.client_id ?? "");
    setEditRecurringTitle(template.title);
    setEditRecurringCadence(template.cadence);
    setEditRecurringNextIssueDate(template.next_issue_date);
    setEditRecurringDueDays(String(template.due_days));
    setEditRecurringEndsOn(template.ends_on ?? "");
    setEditRecurringMaxOccurrences(
      template.max_occurrences ? String(template.max_occurrences) : "",
    );
    setEditRecurringLineItems(
      items.length > 0
        ? items
        : [
            createRecurringLineItem({
              gstRate: workspace.data?.gst_registered ? "15" : "0",
            }),
          ],
    );
    setEditRecurringNotes(
      typeof template.template === "object" &&
        template.template &&
        "notes" in template.template
        ? String((template.template as { notes?: string | null }).notes ?? "")
        : "",
    );
    setEditRecurringPaymentInstructions(
      typeof template.template === "object" &&
        template.template &&
        "payment_instructions" in template.template
        ? String(
            (template.template as { payment_instructions?: string | null })
              .payment_instructions ?? "",
          )
        : "",
    );
  }

  async function saveRecurringTemplate() {
    if (!editingRecurringId || !canUseRecurringInvoices) return;
    await recurringInvoices.update.mutateAsync({
      id: editingRecurringId,
      values: {
        cadence: editRecurringCadence,
        clientId: editRecurringClientId,
        dueDays: editRecurringDueDays,
        endsOn: editRecurringEndsOn,
        lineItems: serializeRecurringLineItems(editRecurringLineItems),
        nextIssueDate: editRecurringNextIssueDate,
        maxOccurrences: editRecurringMaxOccurrences,
        notes: editRecurringNotes,
        paymentInstructions: editRecurringPaymentInstructions,
        title: editRecurringTitle,
      },
    });
    setEditingRecurringId(null);
  }

  async function generateRecurringInvoices() {
    if (!canUseRecurringInvoices) return;
    const result = await recurringInvoices.generateDue.mutateAsync({});
    setRecurringGenerationResult(
      result
        ? `${result.created} invoice${result.created === 1 ? "" : "s"} created, ${result.skipped} skipped.`
        : "No recurring templates were due.",
    );
  }

  return (
    <ProtectedScreen
      title="Invoices"
      subtitle="Create GST-ready invoices, add line items, and track sent, paid, overdue, and recurring work."
    >
      <View style={stylesheet.metricsGrid}>
        <View style={stylesheet.metric}>
          <Card gap="xs">
            <AppText size="xs" tone="muted">
              Draft
            </AppText>
            <AppText size="lg" weight="bold">
              {String(
                records.filter((invoice) => invoice.status === "draft").length,
              )}
            </AppText>
          </Card>
        </View>
        <View style={stylesheet.metric}>
          <Card gap="xs">
            <AppText size="xs" tone="muted">
              Overdue
            </AppText>
            <AppText size="lg" weight="bold">
              {String(
                records.filter((invoice) => invoice.status === "overdue")
                  .length,
              )}
            </AppText>
          </Card>
        </View>
        <View style={stylesheet.metric}>
          <Card gap="xs">
            <AppText size="xs" tone="muted">
              Outstanding
            </AppText>
            <AppText size="lg" weight="bold">
              {formatMoney(outstanding)}
            </AppText>
          </Card>
        </View>
        <View style={stylesheet.metric}>
          <Card gap="xs">
            <AppText size="xs" tone="muted">
              Unbilled time
            </AppText>
            <AppText size="lg" weight="bold">
              {formatMoney(
                allUnbilledTimeEntries.reduce(
                  (total, entry) => total + timeEntryValue(entry),
                  0,
                ),
              )}
            </AppText>
          </Card>
        </View>
      </View>

      {!subscription.canUseRecurringInvoices ? (
        <UpgradePrompt reason="Subscribe to keep recurring invoice generation active." />
      ) : !canManageWorkspace ? (
        <Card gap="xs">
          <AppText weight="semibold">Recurring invoice access</AppText>
          <AppText size="xs" tone="muted">
            Workspace owners and admins manage recurring invoice templates.
          </AppText>
        </Card>
      ) : null}

      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Create draft invoice</AppText>
          <AppText size="xs" tone="muted">
            Reserve the next workspace invoice number and start building line
            items.
          </AppText>
        </Stack>
        <View style={stylesheet.formGrid}>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Client
            </AppText>
            <SearchableSelect
              title="Choose client"
              value={clientId}
              options={clientOptions}
              placeholder="No client"
              onValueChange={setClientId}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Due date
            </AppText>
            <TextField
              accessibilityLabel="Due date"
              value={dueDate}
              placeholder="2026-07-20"
              onChangeText={setDueDate}
            />
          </View>
          <View style={[stylesheet.field, stylesheet.wideField]}>
            <AppText size="xs" weight="semibold">
              Notes
            </AppText>
            <TextField
              accessibilityLabel="Notes"
              value={notes}
              placeholder="Payment instructions or client message"
              onChangeText={setNotes}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Purchase order
            </AppText>
            <TextField
              accessibilityLabel="Purchase order reference"
              value={purchaseOrderReference}
              placeholder="PO-1042"
              onChangeText={setPurchaseOrderReference}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Client reference
            </AppText>
            <TextField
              accessibilityLabel="Client reference"
              value={clientReference}
              placeholder="Job or cost centre"
              onChangeText={setClientReference}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Job starts
            </AppText>
            <TextField
              accessibilityLabel="Job starts on"
              value={jobStartsOn}
              placeholder="YYYY-MM-DD"
              onChangeText={setJobStartsOn}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Job ends
            </AppText>
            <TextField
              accessibilityLabel="Job ends on"
              value={jobEndsOn}
              placeholder="YYYY-MM-DD"
              onChangeText={setJobEndsOn}
            />
          </View>
        </View>
        {errorMessage ? (
          <AppText size="xs" tone="danger">
            {errorMessage}
          </AppText>
        ) : null}
        <Button
          align="start"
          iconName="file-plus"
          disabled={!dueDate.trim() || invoices.create.isPending}
          onPress={() => void createInvoice()}
        >
          <ButtonText>
            {invoices.create.isPending ? "Creating..." : "Create invoice"}
          </ButtonText>
        </Button>
      </Card>

      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Recurring invoice templates</AppText>
          <AppText size="xs" tone="muted">
            Prepare regular billing cycles for retainers, maintenance plans, and
            ongoing client work.
          </AppText>
        </Stack>
        <View style={stylesheet.formGrid}>
          <View style={[stylesheet.field, stylesheet.wideField]}>
            <AppText size="xs" weight="semibold">
              Template title
            </AppText>
            <TextField
              accessibilityLabel="Template title"
              value={recurringTitle}
              placeholder="Monthly content retainer"
              onChangeText={setRecurringTitle}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Client
            </AppText>
            <SearchableSelect
              title="Choose client"
              value={recurringClientId}
              options={clientOptions}
              placeholder="No client"
              onValueChange={setRecurringClientId}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Cadence
            </AppText>
            <SearchableSelect
              title="Choose cadence"
              value={recurringCadence}
              options={CADENCE_OPTIONS}
              onValueChange={setRecurringCadence}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Next issue date
            </AppText>
            <TextField
              accessibilityLabel="Next issue date"
              value={recurringNextIssueDate}
              placeholder="2026-08-01"
              onChangeText={setRecurringNextIssueDate}
            />
          </View>
          <View style={stylesheet.smallField}>
            <AppText size="xs" weight="semibold">
              Due days
            </AppText>
            <TextField
              accessibilityLabel="Due days"
              value={recurringDueDays}
              placeholder="14"
              keyboardType="numeric"
              onChangeText={setRecurringDueDays}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              End date (optional)
            </AppText>
            <TextField
              accessibilityLabel="End date (optional)"
              value={recurringEndsOn}
              placeholder="2027-06-30"
              onChangeText={setRecurringEndsOn}
            />
          </View>
          <View style={stylesheet.smallField}>
            <AppText size="xs" weight="semibold">
              Invoice limit
            </AppText>
            <TextField
              accessibilityLabel="Invoice limit"
              value={recurringMaxOccurrences}
              placeholder="No limit"
              keyboardType="numeric"
              onChangeText={setRecurringMaxOccurrences}
            />
          </View>
          <View style={[stylesheet.field, stylesheet.wideField]}>
            <AppText size="xs" weight="semibold">
              Notes
            </AppText>
            <TextField
              accessibilityLabel="Notes"
              value={recurringNotes}
              placeholder="Default invoice note or scope reminder"
              onChangeText={setRecurringNotes}
            />
          </View>
          <View style={[stylesheet.field, stylesheet.wideField]}>
            <AppText size="xs" weight="semibold">
              Payment instructions
            </AppText>
            <TextField
              accessibilityLabel="Payment instructions"
              value={recurringPaymentInstructions}
              placeholder="Bank account, due wording, or remittance instructions"
              onChangeText={setRecurringPaymentInstructions}
            />
          </View>
        </View>
        <RecurringLineItemEditor
          items={recurringLineItems}
          onAdd={() =>
            addRecurringLine("create", {
              gstRate: workspace.data?.gst_registered ? "15" : "0",
            })
          }
          onChange={(index, field, value) =>
            updateRecurringLine("create", index, { [field]: value })
          }
          onRemove={(index) => removeRecurringLine("create", index)}
        />
        <View style={stylesheet.statusControls}>
          <Button
            align="start"
            iconName="repeat"
            disabled={
              !recurringTitle.trim() ||
              !hasRecurringLineItem(recurringLineItems) ||
              !canUseRecurringInvoices ||
              recurringInvoices.create.isPending
            }
            onPress={() => void createRecurringInvoice()}
          >
            <ButtonText>
              {recurringInvoices.create.isPending
                ? "Creating..."
                : "Create recurring template"}
            </ButtonText>
          </Button>
          <Button
            align="start"
            iconName="file-plus"
            variant="outline"
            disabled={
              !canUseRecurringInvoices ||
              recurringInvoices.generateDue.isPending
            }
            onPress={() => void generateRecurringInvoices()}
          >
            <ButtonText>
              {recurringInvoices.generateDue.isPending
                ? "Generating..."
                : "Generate due invoices"}
            </ButtonText>
          </Button>
        </View>
        {recurringGenerationResult ? (
          <AppText size="xs" tone="muted">
            {recurringGenerationResult}
          </AppText>
        ) : null}
        {(recurringInvoices.data ?? []).length === 0 ? (
          <View style={stylesheet.emptyState}>
            <AppText size="sm">
              No recurring templates yet. Automation will issue invoices from
              these schedules.
            </AppText>
          </View>
        ) : (
          <Stack gap="sm">
            {(recurringInvoices.data ?? []).map((template) => {
              const lineItems = recurringTemplateLineItems(template.template);
              const scheduleComplete = Boolean(
                (template.max_occurrences &&
                  template.generated_count >= template.max_occurrences) ||
                (template.ends_on &&
                  template.next_issue_date > template.ends_on),
              );
              return (
                <View key={template.id} style={stylesheet.itemRow}>
                  {editingRecurringId === template.id ? (
                    <View style={stylesheet.editPanel}>
                      <View style={stylesheet.formGrid}>
                        <View style={[stylesheet.field, stylesheet.wideField]}>
                          <AppText size="xs" weight="semibold">
                            Template title
                          </AppText>
                          <TextField
                            accessibilityLabel="Template title"
                            value={editRecurringTitle}
                            placeholder="Monthly content retainer"
                            onChangeText={setEditRecurringTitle}
                          />
                        </View>
                        <View style={stylesheet.field}>
                          <AppText size="xs" weight="semibold">
                            Client
                          </AppText>
                          <SearchableSelect
                            title="Choose client"
                            value={editRecurringClientId}
                            options={clientOptions}
                            placeholder="No client"
                            onValueChange={setEditRecurringClientId}
                          />
                        </View>
                        <View style={stylesheet.field}>
                          <AppText size="xs" weight="semibold">
                            Cadence
                          </AppText>
                          <SearchableSelect
                            title="Choose cadence"
                            value={editRecurringCadence}
                            options={CADENCE_OPTIONS}
                            onValueChange={setEditRecurringCadence}
                          />
                        </View>
                        <View style={stylesheet.field}>
                          <AppText size="xs" weight="semibold">
                            Next issue date
                          </AppText>
                          <TextField
                            accessibilityLabel="Next issue date"
                            value={editRecurringNextIssueDate}
                            placeholder="2026-08-01"
                            onChangeText={setEditRecurringNextIssueDate}
                          />
                        </View>
                        <View style={stylesheet.smallField}>
                          <AppText size="xs" weight="semibold">
                            Due days
                          </AppText>
                          <TextField
                            accessibilityLabel="Due days"
                            value={editRecurringDueDays}
                            keyboardType="numeric"
                            placeholder="14"
                            onChangeText={setEditRecurringDueDays}
                          />
                        </View>
                        <View style={stylesheet.field}>
                          <AppText size="xs" weight="semibold">
                            End date (optional)
                          </AppText>
                          <TextField
                            accessibilityLabel="End date (optional)"
                            value={editRecurringEndsOn}
                            placeholder="2027-06-30"
                            onChangeText={setEditRecurringEndsOn}
                          />
                        </View>
                        <View style={stylesheet.smallField}>
                          <AppText size="xs" weight="semibold">
                            Invoice limit
                          </AppText>
                          <TextField
                            accessibilityLabel="Invoice limit"
                            value={editRecurringMaxOccurrences}
                            placeholder="No limit"
                            keyboardType="numeric"
                            onChangeText={setEditRecurringMaxOccurrences}
                          />
                        </View>
                        <View style={[stylesheet.field, stylesheet.wideField]}>
                          <AppText size="xs" weight="semibold">
                            Notes
                          </AppText>
                          <TextField
                            accessibilityLabel="Notes"
                            value={editRecurringNotes}
                            placeholder="Default invoice note or scope reminder"
                            onChangeText={setEditRecurringNotes}
                          />
                        </View>
                        <View style={[stylesheet.field, stylesheet.wideField]}>
                          <AppText size="xs" weight="semibold">
                            Payment instructions
                          </AppText>
                          <TextField
                            accessibilityLabel="Payment instructions"
                            value={editRecurringPaymentInstructions}
                            placeholder="Bank account, due wording, or remittance instructions"
                            onChangeText={setEditRecurringPaymentInstructions}
                          />
                        </View>
                      </View>
                      <RecurringLineItemEditor
                        items={editRecurringLineItems}
                        onAdd={() =>
                          addRecurringLine("edit", {
                            gstRate: workspace.data?.gst_registered
                              ? "15"
                              : "0",
                          })
                        }
                        onChange={(index, field, value) =>
                          updateRecurringLine("edit", index, {
                            [field]: value,
                          })
                        }
                        onRemove={(index) => removeRecurringLine("edit", index)}
                      />
                      <View style={stylesheet.statusControls}>
                        <Button
                          compact
                          iconName="save"
                          disabled={
                            !editRecurringTitle.trim() ||
                            !hasRecurringLineItem(editRecurringLineItems) ||
                            recurringInvoices.update.isPending
                          }
                          onPress={() => void saveRecurringTemplate()}
                        >
                          <ButtonText>
                            {recurringInvoices.update.isPending
                              ? "Saving..."
                              : "Save"}
                          </ButtonText>
                        </Button>
                        <Button
                          compact
                          variant="outline"
                          disabled={recurringInvoices.update.isPending}
                          onPress={() => setEditingRecurringId(null)}
                        >
                          <ButtonText>Cancel</ButtonText>
                        </Button>
                      </View>
                    </View>
                  ) : (
                    <>
                      <View style={stylesheet.itemMain}>
                        <AppText weight="semibold">{template.title}</AppText>
                        <AppText size="xs" tone="muted">
                          {template.cadence} - next {template.next_issue_date} -
                          due in {template.due_days} days
                        </AppText>
                        <AppText size="xs" tone="muted">
                          {template.generated_count} generated
                          {template.max_occurrences
                            ? ` of ${template.max_occurrences}`
                            : ""}
                          {template.ends_on
                            ? ` - ends ${template.ends_on}`
                            : " - no end date"}
                        </AppText>
                        <AppText size="xs" tone="muted">
                          {lineItems.length === 0
                            ? "No line items"
                            : `${lineItems.length} line item${
                                lineItems.length === 1 ? "" : "s"
                              } - ${lineItems[0]?.description ?? ""}`}
                        </AppText>
                        {typeof template.template === "object" &&
                        template.template &&
                        "payment_instructions" in template.template &&
                        (
                          template.template as {
                            payment_instructions?: string | null;
                          }
                        ).payment_instructions ? (
                          <AppText size="xs" tone="muted">
                            Custom payment instructions
                          </AppText>
                        ) : null}
                        <AppText size="xs" tone="muted">
                          {template.client_id
                            ? (clientById.get(template.client_id)?.name ??
                              "Unknown client")
                            : "No client"}
                        </AppText>
                      </View>
                      <View style={stylesheet.statusControls}>
                        <Button
                          compact
                          iconName="pencil"
                          variant="outline"
                          disabled={
                            !canUseRecurringInvoices ||
                            recurringInvoices.update.isPending
                          }
                          onPress={() => startRecurringEdit(template)}
                        >
                          <ButtonText>Edit</ButtonText>
                        </Button>
                        <Button
                          compact
                          variant="outline"
                          disabled={
                            !canUseRecurringInvoices ||
                            scheduleComplete ||
                            recurringInvoices.setEnabled.isPending
                          }
                          onPress={() =>
                            void recurringInvoices.setEnabled.mutateAsync({
                              enabled: !template.enabled,
                              id: template.id,
                            })
                          }
                        >
                          <ButtonText>
                            {scheduleComplete
                              ? "Completed"
                              : template.enabled
                                ? "Pause"
                                : "Resume"}
                          </ButtonText>
                        </Button>
                      </View>
                    </>
                  )}
                </View>
              );
            })}
          </Stack>
        )}
      </Card>

      <View style={stylesheet.invoiceGrid}>
        <Card gap="md">
          <View style={stylesheet.listHeader}>
            <AppText weight="semibold">Invoices</AppText>
            <AppText size="xs" tone="muted">
              {invoices.isLoading
                ? "Loading..."
                : String(filteredInvoices.length)}
            </AppText>
          </View>
          <View style={stylesheet.formGrid}>
            <TextField
              accessibilityLabel="Search invoices"
              value={invoiceSearch}
              placeholder="Search invoice or client"
              onChangeText={setInvoiceSearch}
            />
            <SearchableSelect
              title="Filter invoice status"
              value={invoiceStatusFilter}
              options={[
                { label: "All statuses", value: "" },
                ...INVOICE_STATUS_ACTIONS,
              ]}
              placeholder="All statuses"
              onValueChange={setInvoiceStatusFilter}
            />
          </View>
          {filteredInvoices.length === 0 ? (
            <View style={stylesheet.emptyState}>
              <AppText size="sm">
                {records.length === 0
                  ? "Create a draft invoice before adding line items."
                  : "No invoices match these filters."}
              </AppText>
            </View>
          ) : (
            <Stack gap="sm">
              {filteredInvoices.map((invoice) => {
                const active = selectedInvoice?.id === invoice.id;
                return (
                  <Button
                    key={invoice.id}
                    variant={active ? "primary" : "outline"}
                    align="stretch"
                    onPress={() => setSelectedInvoiceId(invoice.id)}
                  >
                    <View style={stylesheet.invoiceButtonContent}>
                      <View style={stylesheet.invoiceButtonText}>
                        <ButtonText>{invoice.invoice_number}</ButtonText>
                        <AppText size="xs" tone="muted">
                          {invoice.client_id
                            ? (clientById.get(invoice.client_id)?.name ??
                              "Unknown client")
                            : "No client"}
                        </AppText>
                        <AppText size="xs" tone="muted">
                          {invoice.due_date
                            ? `Due ${invoice.due_date}`
                            : "No due date"}
                        </AppText>
                        <AppText size="xs" tone="muted">
                          {invoice.status}
                        </AppText>
                      </View>
                      <AppText size="xs" weight="semibold">
                        {formatMoney(invoice.total_cents)}
                      </AppText>
                    </View>
                  </Button>
                );
              })}
            </Stack>
          )}
        </Card>

        <Card gap="md">
          <Stack gap="xs">
            <AppText weight="semibold">
              {selectedInvoice
                ? `Line items for ${selectedInvoice.invoice_number}`
                : "Line items"}
            </AppText>
            <AppText size="xs" tone="muted">
              Totals are recalculated by the server.
            </AppText>
          </Stack>

          {selectedInvoice ? (
            <View style={stylesheet.detailPanel}>
              <View style={stylesheet.listHeader}>
                <Stack gap="xs">
                  <AppText weight="semibold">Invoice details</AppText>
                  <AppText size="xs" tone="muted">
                    {selectedClient?.name ?? "No client"} - due{" "}
                    {selectedInvoice.due_date ?? "not set"}
                  </AppText>
                </Stack>
                {!invoiceEditOpen ? (
                  <Button
                    compact
                    iconName="pencil"
                    variant="outline"
                    disabled={invoices.update.isPending}
                    onPress={startInvoiceEdit}
                  >
                    <ButtonText>Edit</ButtonText>
                  </Button>
                ) : null}
              </View>
              {invoiceEditOpen ? (
                <Stack gap="sm">
                  <View style={stylesheet.formGrid}>
                    <View style={stylesheet.field}>
                      <AppText size="xs" weight="semibold">
                        Client
                      </AppText>
                      <SearchableSelect
                        title="Choose client"
                        value={editInvoiceClientId}
                        options={clientOptions}
                        placeholder="No client"
                        onValueChange={setEditInvoiceClientId}
                      />
                    </View>
                    <View style={stylesheet.field}>
                      <AppText size="xs" weight="semibold">
                        Due date
                      </AppText>
                      <TextField
                        accessibilityLabel="Due date"
                        value={editInvoiceDueDate}
                        placeholder="2026-07-20"
                        onChangeText={setEditInvoiceDueDate}
                      />
                    </View>
                    <View style={[stylesheet.field, stylesheet.wideField]}>
                      <AppText size="xs" weight="semibold">
                        Notes
                      </AppText>
                      <TextField
                        accessibilityLabel="Notes"
                        value={editInvoiceNotes}
                        placeholder="Payment instructions or client message"
                        onChangeText={setEditInvoiceNotes}
                      />
                    </View>
                    <View style={stylesheet.field}>
                      <AppText size="xs" weight="semibold">
                        Purchase order
                      </AppText>
                      <TextField
                        accessibilityLabel="Purchase order reference"
                        value={editInvoicePurchaseOrderReference}
                        placeholder="PO-1042"
                        onChangeText={setEditInvoicePurchaseOrderReference}
                      />
                    </View>
                    <View style={stylesheet.field}>
                      <AppText size="xs" weight="semibold">
                        Client reference
                      </AppText>
                      <TextField
                        accessibilityLabel="Client reference"
                        value={editInvoiceClientReference}
                        placeholder="Job or cost centre"
                        onChangeText={setEditInvoiceClientReference}
                      />
                    </View>
                    <View style={stylesheet.field}>
                      <AppText size="xs" weight="semibold">
                        Job starts
                      </AppText>
                      <TextField
                        accessibilityLabel="Job starts on"
                        value={editInvoiceJobStartsOn}
                        placeholder="YYYY-MM-DD"
                        onChangeText={setEditInvoiceJobStartsOn}
                      />
                    </View>
                    <View style={stylesheet.field}>
                      <AppText size="xs" weight="semibold">
                        Job ends
                      </AppText>
                      <TextField
                        accessibilityLabel="Job ends on"
                        value={editInvoiceJobEndsOn}
                        placeholder="YYYY-MM-DD"
                        onChangeText={setEditInvoiceJobEndsOn}
                      />
                    </View>
                    <View style={[stylesheet.field, stylesheet.wideField]}>
                      <AppText size="xs" weight="semibold">
                        Amendment reason
                      </AppText>
                      <TextField
                        accessibilityLabel="Amendment reason"
                        value={editInvoiceAmendmentReason}
                        placeholder="Optional audit note for this change"
                        onChangeText={setEditInvoiceAmendmentReason}
                      />
                    </View>
                  </View>
                  <View style={stylesheet.statusControls}>
                    <Button
                      compact
                      iconName="save"
                      disabled={
                        !editInvoiceDueDate.trim() || invoices.update.isPending
                      }
                      onPress={() => void saveInvoiceDetails()}
                    >
                      <ButtonText>
                        {invoices.update.isPending ? "Saving..." : "Save"}
                      </ButtonText>
                    </Button>
                    <Button
                      compact
                      variant="outline"
                      disabled={invoices.update.isPending}
                      onPress={() => setInvoiceEditOpen(false)}
                    >
                      <ButtonText>Cancel</ButtonText>
                    </Button>
                  </View>
                </Stack>
              ) : null}
            </View>
          ) : null}

          {selectedInvoice ? (
            <View style={stylesheet.statusControls}>
              <Button
                compact
                iconName="external-link"
                variant="outline"
                onPress={() =>
                  router.push(`/invoices/${selectedInvoice.id}` as never)
                }
              >
                <ButtonText>View details</ButtonText>
              </Button>
              <Button
                compact
                iconName="send"
                disabled={
                  !selectedClient?.email ||
                  selectedItems.length === 0 ||
                  invoices.sendEmail.isPending
                }
                onPress={() =>
                  void invoices.sendEmail.mutateAsync(selectedInvoice.id)
                }
              >
                <ButtonText>
                  {invoices.sendEmail.isPending ? "Sending..." : "Send invoice"}
                </ButtonText>
              </Button>
              {INVOICE_STATUS_ACTIONS.map((status) => (
                <Button
                  key={status.value}
                  compact
                  variant={
                    selectedInvoice.status === status.value
                      ? "primary"
                      : "outline"
                  }
                  disabled={invoices.updateStatus.isPending}
                  onPress={() =>
                    void invoices.updateStatus.mutateAsync({
                      id: selectedInvoice.id,
                      status: status.value,
                    })
                  }
                >
                  <ButtonText>{status.label}</ButtonText>
                </Button>
              ))}
              {!selectedClient?.email ? (
                <AppText size="xs" tone="muted">
                  Add a client billing email before sending.
                </AppText>
              ) : null}
            </View>
          ) : null}

          <View style={stylesheet.formGrid}>
            <View style={[stylesheet.field, stylesheet.wideField]}>
              <AppText size="xs" weight="semibold">
                Description
              </AppText>
              <TextField
                accessibilityLabel="Description"
                value={description}
                placeholder="Design, development, consulting..."
                onChangeText={setDescription}
              />
            </View>
            <View style={stylesheet.smallField}>
              <AppText size="xs" weight="semibold">
                Qty
              </AppText>
              <TextField
                accessibilityLabel="Qty"
                value={quantity}
                placeholder="1"
                keyboardType="numeric"
                onChangeText={setQuantity}
              />
            </View>
            <View style={stylesheet.smallField}>
              <AppText size="xs" weight="semibold">
                Rate
              </AppText>
              <TextField
                accessibilityLabel="Rate"
                value={unitAmount}
                placeholder="120"
                keyboardType="numeric"
                onChangeText={setUnitAmount}
              />
            </View>
            <View style={stylesheet.smallField}>
              <AppText size="xs" weight="semibold">
                GST %
              </AppText>
              <TextField
                accessibilityLabel="GST %"
                value={gstRate}
                placeholder="15"
                keyboardType="numeric"
                onChangeText={setGstRate}
              />
            </View>
          </View>
          <Button
            align="start"
            iconName="plus"
            disabled={!canAddItem || invoiceItems.createManualItem.isPending}
            onPress={() => void addLineItem()}
          >
            <ButtonText>
              {invoiceItems.createManualItem.isPending
                ? "Adding..."
                : "Add line item"}
            </ButtonText>
          </Button>

          {selectedItems.length === 0 ? (
            <View style={stylesheet.emptyState}>
              <AppText size="sm">No line items on this invoice yet.</AppText>
            </View>
          ) : (
            <Stack gap="sm">
              {selectedItems.map((item) => (
                <View key={item.id} style={stylesheet.itemRow}>
                  {editingItemId === item.id ? (
                    <View style={stylesheet.editPanel}>
                      <View style={stylesheet.formGrid}>
                        <View style={[stylesheet.field, stylesheet.wideField]}>
                          <AppText size="xs" weight="semibold">
                            Description
                          </AppText>
                          <TextField
                            accessibilityLabel="Description"
                            value={editItemDescription}
                            placeholder="Design, development, consulting..."
                            onChangeText={setEditItemDescription}
                          />
                        </View>
                        <View style={stylesheet.smallField}>
                          <AppText size="xs" weight="semibold">
                            Qty
                          </AppText>
                          <TextField
                            accessibilityLabel="Qty"
                            value={editItemQuantity}
                            keyboardType="numeric"
                            placeholder="1"
                            onChangeText={setEditItemQuantity}
                          />
                        </View>
                        <View style={stylesheet.smallField}>
                          <AppText size="xs" weight="semibold">
                            Rate
                          </AppText>
                          <TextField
                            accessibilityLabel="Rate"
                            value={editItemUnitAmount}
                            keyboardType="numeric"
                            placeholder="120"
                            onChangeText={setEditItemUnitAmount}
                          />
                        </View>
                        <View style={stylesheet.smallField}>
                          <AppText size="xs" weight="semibold">
                            GST %
                          </AppText>
                          <TextField
                            accessibilityLabel="GST %"
                            value={editItemGstRate}
                            keyboardType="numeric"
                            placeholder="15"
                            onChangeText={setEditItemGstRate}
                          />
                        </View>
                      </View>
                      <View style={stylesheet.statusControls}>
                        <Button
                          compact
                          iconName="save"
                          disabled={
                            !editItemDescription.trim() ||
                            invoiceItems.updateManualItem.isPending
                          }
                          onPress={() => void saveLineItem(item)}
                        >
                          <ButtonText>
                            {invoiceItems.updateManualItem.isPending
                              ? "Saving..."
                              : "Save"}
                          </ButtonText>
                        </Button>
                        <Button
                          compact
                          variant="outline"
                          disabled={invoiceItems.updateManualItem.isPending}
                          onPress={() => setEditingItemId(null)}
                        >
                          <ButtonText>Cancel</ButtonText>
                        </Button>
                      </View>
                    </View>
                  ) : (
                    <>
                      <View style={stylesheet.itemMain}>
                        <AppText weight="semibold">{item.description}</AppText>
                        <AppText size="xs" tone="muted">
                          {item.quantity} x{" "}
                          {formatMoney(item.unit_amount_cents)} - GST{" "}
                          {item.gst_rate}%
                        </AppText>
                        {item.source !== "manual" ? (
                          <AppText size="xs" tone="muted">
                            Converted from tracked time
                          </AppText>
                        ) : null}
                      </View>
                      <View style={stylesheet.itemTotals}>
                        <AppText size="xs" tone="muted">
                          GST {formatMoney(lineGst(item))}
                        </AppText>
                        <AppText weight="semibold">
                          {formatMoney(lineSubtotal(item) + lineGst(item))}
                        </AppText>
                      </View>
                      {item.source === "manual" && !item.expense_id ? (
                        <View style={stylesheet.statusControls}>
                          <Button
                            compact
                            iconName="pencil"
                            variant="outline"
                            disabled={invoiceItems.updateManualItem.isPending}
                            onPress={() => startLineItemEdit(item)}
                          >
                            <ButtonText>Edit</ButtonText>
                          </Button>
                          <Button
                            compact
                            iconName="trash-2"
                            variant="danger"
                            disabled={invoiceItems.deleteManualItem.isPending}
                            onPress={() =>
                              void invoiceItems.deleteManualItem.mutateAsync({
                                id: item.id,
                                invoiceId: item.invoice_id,
                              })
                            }
                          >
                            <ButtonText tone="danger">
                              {invoiceItems.deleteManualItem.isPending
                                ? "Removing..."
                                : "Remove"}
                            </ButtonText>
                          </Button>
                        </View>
                      ) : null}
                    </>
                  )}
                </View>
              ))}
            </Stack>
          )}

          {selectedInvoice ? (
            <View style={stylesheet.invoiceTotals}>
              <View style={stylesheet.totalRow}>
                <AppText>Subtotal</AppText>
                <AppText weight="semibold">
                  {formatMoney(selectedInvoice.subtotal_cents)}
                </AppText>
              </View>
              <View style={stylesheet.totalRow}>
                <AppText>GST</AppText>
                <AppText weight="semibold">
                  {formatMoney(selectedInvoice.gst_cents)}
                </AppText>
              </View>
              {selectedInvoice.rounding_adjustment_cents !== 0 ? (
                <View style={stylesheet.totalRow}>
                  <AppText>Rounding</AppText>
                  <AppText weight="semibold">
                    {formatMoney(selectedInvoice.rounding_adjustment_cents)}
                  </AppText>
                </View>
              ) : null}
              <View style={stylesheet.totalRow}>
                <AppText weight="bold">Total</AppText>
                <AppText weight="bold">
                  {formatMoney(selectedInvoice.total_cents)}
                </AppText>
              </View>
            </View>
          ) : null}

          {selectedInvoice ? (
            <View style={stylesheet.detailPanel}>
              <Stack gap="xs">
                <AppText weight="semibold">
                  Payment, refunds, and amendments
                </AppText>
                <AppText size="xs" tone="muted">
                  Record payments, then capture refunds and amendment notes
                  against the invoice history.
                </AppText>
              </Stack>
              <View style={stylesheet.formGrid}>
                <View style={stylesheet.smallField}>
                  <AppText size="xs" weight="semibold">
                    Payment
                  </AppText>
                  <TextField
                    accessibilityLabel="Payment"
                    value={paymentAmount}
                    placeholder={(remainingBalance / 100).toFixed(2)}
                    keyboardType="numeric"
                    onChangeText={setPaymentAmount}
                  />
                </View>
                <View style={stylesheet.field}>
                  <AppText size="xs" weight="semibold">
                    Paid on
                  </AppText>
                  <TextField
                    accessibilityLabel="Paid on"
                    value={paymentDate}
                    placeholder="2026-07-04"
                    onChangeText={setPaymentDate}
                  />
                </View>
                <View style={stylesheet.field}>
                  <AppText size="xs" weight="semibold">
                    Reference
                  </AppText>
                  <TextField
                    accessibilityLabel="Reference"
                    value={paymentReference}
                    placeholder="Bank transfer, Stripe payout..."
                    onChangeText={setPaymentReference}
                  />
                </View>
                <View style={[stylesheet.field, stylesheet.wideField]}>
                  <AppText size="xs" weight="semibold">
                    Payment note
                  </AppText>
                  <TextField
                    accessibilityLabel="Payment note"
                    value={paymentNote}
                    placeholder="Deposit for July retainer, reconciled in Kiwibank..."
                    onChangeText={setPaymentNote}
                  />
                </View>
                <View style={stylesheet.smallField}>
                  <AppText size="xs" weight="semibold">
                    Refund
                  </AppText>
                  <TextField
                    accessibilityLabel="Refund"
                    value={refundAmount}
                    placeholder={(remainingRefundable / 100).toFixed(2)}
                    keyboardType="numeric"
                    onChangeText={setRefundAmount}
                  />
                </View>
                <View style={[stylesheet.field, stylesheet.wideField]}>
                  <AppText size="xs" weight="semibold">
                    Reason
                  </AppText>
                  <TextField
                    accessibilityLabel="Reason"
                    value={refundReason}
                    placeholder="Client credit, duplicate payment, scope change..."
                    onChangeText={setRefundReason}
                  />
                </View>
              </View>
              <View style={stylesheet.statusControls}>
                <Button
                  compact
                  iconName="badge-dollar-sign"
                  variant="outline"
                  disabled={
                    !paymentAmount.trim() ||
                    remainingBalance <= 0 ||
                    ["void", "refunded"].includes(selectedInvoice.status) ||
                    invoiceLifecycle.createPayment.isPending
                  }
                  onPress={() => void createPayment()}
                >
                  <ButtonText>
                    {invoiceLifecycle.createPayment.isPending
                      ? "Recording..."
                      : "Record payment"}
                  </ButtonText>
                </Button>
                <Button
                  compact
                  iconName="rotate-ccw"
                  variant="outline"
                  disabled={
                    !refundAmount.trim() ||
                    remainingRefundable <= 0 ||
                    !["paid", "refunded"].includes(selectedInvoice.status) ||
                    invoiceLifecycle.createRefund.isPending
                  }
                  onPress={() => void createRefund()}
                >
                  <ButtonText>
                    {invoiceLifecycle.createRefund.isPending
                      ? "Recording..."
                      : "Record refund"}
                  </ButtonText>
                </Button>
                {["void", "refunded"].includes(selectedInvoice.status) ? (
                  <AppText size="xs" tone="muted">
                    Void or fully refunded invoices cannot take payments.
                  </AppText>
                ) : null}
                {selectedInvoice.status !== "paid" &&
                selectedInvoice.status !== "refunded" ? (
                  <AppText size="xs" tone="muted">
                    Mark the invoice paid before recording refunds.
                  </AppText>
                ) : null}
              </View>
              <Stack gap="sm">
                <View style={stylesheet.totalRow}>
                  <AppText>Paid</AppText>
                  <AppText weight="semibold">{formatMoney(paidTotal)}</AppText>
                </View>
                <View style={stylesheet.totalRow}>
                  <AppText>Remaining balance</AppText>
                  <AppText weight="semibold">
                    {formatMoney(remainingBalance)}
                  </AppText>
                </View>
                <View style={stylesheet.totalRow}>
                  <AppText>Refunded</AppText>
                  <AppText weight="semibold">
                    {formatMoney(refundedTotal)}
                  </AppText>
                </View>
                <View style={stylesheet.totalRow}>
                  <AppText>Remaining refundable</AppText>
                  <AppText weight="semibold">
                    {formatMoney(remainingRefundable)}
                  </AppText>
                </View>
              </Stack>
              {selectedPayments.length > 0 ? (
                <Stack gap="sm">
                  {selectedPayments.map((payment) => (
                    <View key={payment.id} style={stylesheet.historyRow}>
                      <AppText weight="semibold">
                        {formatMoney(payment.amount_cents)}
                      </AppText>
                      <AppText size="xs" tone="muted">
                        {new Date(payment.paid_at).toLocaleDateString()}
                        {payment.reference ? ` - ${payment.reference}` : ""}
                      </AppText>
                      {payment.note ? (
                        <AppText size="xs" tone="muted">
                          {payment.note}
                        </AppText>
                      ) : null}
                    </View>
                  ))}
                </Stack>
              ) : null}
              {selectedRefunds.length > 0 ? (
                <Stack gap="sm">
                  {selectedRefunds.map((refund) => (
                    <View key={refund.id} style={stylesheet.historyRow}>
                      <AppText weight="semibold">
                        {formatMoney(refund.amount_cents)}
                      </AppText>
                      <AppText size="xs" tone="muted">
                        {new Date(refund.refunded_at).toLocaleDateString()} -{" "}
                        {refund.reason ?? "No reason"}
                      </AppText>
                    </View>
                  ))}
                </Stack>
              ) : null}
              {selectedAmendments.length > 0 ? (
                <Stack gap="sm">
                  <AppText weight="semibold">Amendments</AppText>
                  {selectedAmendments.map((amendment) => (
                    <View key={amendment.id} style={stylesheet.historyRow}>
                      <AppText weight="semibold">{amendment.reason}</AppText>
                      <AppText size="xs" tone="muted">
                        {new Date(amendment.created_at).toLocaleString()}
                      </AppText>
                    </View>
                  ))}
                </Stack>
              ) : null}
            </View>
          ) : null}
        </Card>
      </View>

      {!subscription.canUseAutomatedReminders ? (
        <UpgradePrompt reason="Subscribe to keep automated overdue reminders active." />
      ) : !canManageWorkspace ? (
        <Card gap="xs">
          <AppText weight="semibold">Reminder access</AppText>
          <AppText size="xs" tone="muted">
            Workspace owners and admins manage automated reminder schedules.
          </AppText>
        </Card>
      ) : null}

      <Card gap="md">
        <View style={stylesheet.listHeader}>
          <Stack gap="xs">
            <AppText weight="semibold">Reminder schedule</AppText>
            <AppText size="xs" tone="muted">
              Schedule follow-up reminders for the selected invoice. Delivery
              automation will use these records.
            </AppText>
          </Stack>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Reminder date
            </AppText>
            <TextField
              accessibilityLabel="Reminder date"
              value={reminderDate}
              placeholder="2026-07-20"
              onChangeText={setReminderDate}
            />
          </View>
        </View>
        <Button
          align="start"
          iconName="bell-plus"
          disabled={
            !selectedInvoice ||
            !reminderDate.trim() ||
            !canUseAutomatedReminders ||
            invoiceReminders.create.isPending
          }
          onPress={() => void scheduleReminder()}
        >
          <ButtonText>
            {invoiceReminders.create.isPending
              ? "Scheduling..."
              : "Schedule reminder"}
          </ButtonText>
        </Button>
        <Button
          align="start"
          iconName="send"
          variant="outline"
          disabled={
            !canUseAutomatedReminders || invoiceReminders.sendDue.isPending
          }
          onPress={() => void sendDueReminders()}
        >
          <ButtonText>
            {invoiceReminders.sendDue.isPending
              ? "Sending reminders..."
              : selectedInvoice
                ? "Send due reminders for selected invoice"
                : "Send due workspace reminders"}
          </ButtonText>
        </Button>
        {reminderDeliveryResult ? (
          <AppText size="xs" tone="muted">
            {reminderDeliveryResult}
          </AppText>
        ) : null}
        {!selectedInvoice ? (
          <View style={stylesheet.emptyState}>
            <AppText size="sm">
              Select an invoice before scheduling reminders.
            </AppText>
          </View>
        ) : selectedReminders.length === 0 ? (
          <View style={stylesheet.emptyState}>
            <AppText size="sm">
              No reminders are scheduled for this invoice.
            </AppText>
          </View>
        ) : (
          <Stack gap="sm">
            {selectedReminders.map((reminder) => (
              <View key={reminder.id} style={stylesheet.itemRow}>
                {editingReminderId === reminder.id ? (
                  <View style={stylesheet.editPanel}>
                    <View style={stylesheet.formGrid}>
                      <View style={stylesheet.field}>
                        <AppText size="xs" weight="semibold">
                          Reminder date
                        </AppText>
                        <TextField
                          accessibilityLabel="Reminder date"
                          value={editReminderDate}
                          placeholder="2026-07-20"
                          onChangeText={setEditReminderDate}
                        />
                      </View>
                    </View>
                    <View style={stylesheet.statusControls}>
                      <Button
                        compact
                        iconName="save"
                        disabled={
                          !editReminderDate.trim() ||
                          invoiceReminders.update.isPending
                        }
                        onPress={() => void saveReminder(reminder)}
                      >
                        <ButtonText>
                          {invoiceReminders.update.isPending
                            ? "Saving..."
                            : "Save"}
                        </ButtonText>
                      </Button>
                      <Button
                        compact
                        variant="outline"
                        disabled={invoiceReminders.update.isPending}
                        onPress={() => setEditingReminderId(null)}
                      >
                        <ButtonText>Cancel</ButtonText>
                      </Button>
                    </View>
                  </View>
                ) : (
                  <>
                    <View style={stylesheet.itemMain}>
                      <AppText weight="semibold">
                        {new Date(reminder.scheduled_for).toLocaleDateString()}
                      </AppText>
                      <AppText size="xs" tone="muted">
                        {reminder.status}
                        {reminder.sent_at ? ` - sent ${reminder.sent_at}` : ""}
                      </AppText>
                      {reminder.delivery_error ? (
                        <AppText size="xs" tone="danger">
                          {reminder.delivery_error}
                        </AppText>
                      ) : null}
                    </View>
                    {reminder.status === "scheduled" ? (
                      <View style={stylesheet.statusControls}>
                        <Button
                          compact
                          iconName="pencil"
                          variant="outline"
                          disabled={
                            !canUseAutomatedReminders ||
                            invoiceReminders.update.isPending
                          }
                          onPress={() => startReminderEdit(reminder)}
                        >
                          <ButtonText>Edit</ButtonText>
                        </Button>
                        <Button
                          compact
                          variant="outline"
                          disabled={
                            !canUseAutomatedReminders ||
                            invoiceReminders.cancel.isPending
                          }
                          onPress={() =>
                            void invoiceReminders.cancel.mutateAsync(
                              reminder.id,
                            )
                          }
                        >
                          <ButtonText>Cancel</ButtonText>
                        </Button>
                      </View>
                    ) : null}
                  </>
                )}
              </View>
            ))}
          </Stack>
        )}
      </Card>

      <Card gap="md">
        <View style={stylesheet.listHeader}>
          <Stack gap="xs">
            <AppText weight="semibold">Billable time ready to invoice</AppText>
            <AppText size="xs" tone="muted">
              Convert saved time entries into invoice line items and mark them
              invoiced. Quantities use workspace time rounding (
              {timeRoundingLabel(workspace.data?.default_time_rounding)}).
            </AppText>
          </Stack>
          <View style={stylesheet.smallField}>
            <AppText size="xs" weight="semibold">
              GST %
            </AppText>
            <TextField
              accessibilityLabel="GST %"
              value={timeEntryGstRate}
              placeholder="15"
              keyboardType="numeric"
              onChangeText={setTimeEntryGstRate}
            />
          </View>
        </View>
        {!selectedInvoice ? (
          <View style={stylesheet.emptyState}>
            <AppText size="sm">Create or select an invoice first.</AppText>
          </View>
        ) : allUnbilledTimeEntries.length === 0 ? (
          <View style={stylesheet.emptyState}>
            <AppText size="sm">No unbilled billable time is ready.</AppText>
          </View>
        ) : invoiceReadyTimeEntries.length === 0 ? (
          <View style={stylesheet.emptyState}>
            <AppText size="sm">
              No ready time matches this invoice client.
            </AppText>
            <AppText size="xs" tone="muted">
              {otherClientTimeEntryCount} billable entr
              {otherClientTimeEntryCount === 1 ? "y is" : "ies are"} linked to
              other clients or projects.
            </AppText>
          </View>
        ) : (
          <Stack gap="sm">
            {timeClientFocus.length > 0 ? (
              <View style={stylesheet.quickDraftPanel}>
                <Stack gap="xs">
                  <AppText size="xs" weight="semibold">
                    Start from unbilled time
                  </AppText>
                  <AppText size="xs" tone="muted">
                    Create a client draft, then add matching time rows below.
                  </AppText>
                </Stack>
                <View style={stylesheet.quickDraftGrid}>
                  {timeClientFocus.map(([targetClientId, summary]) => (
                    <View key={targetClientId} style={stylesheet.quickDraftRow}>
                      <View style={stylesheet.itemMain}>
                        <AppText weight="semibold">
                          {clientById.get(targetClientId)?.name ??
                            "Unknown client"}
                        </AppText>
                        <AppText size="xs" tone="muted">
                          {summary.count} entr
                          {summary.count === 1 ? "y" : "ies"} -{" "}
                          {formatDuration(summary.seconds)} -{" "}
                          {formatMoney(summary.value)}
                        </AppText>
                      </View>
                      <Button
                        compact
                        variant="outline"
                        disabled={invoices.create.isPending}
                        onPress={() =>
                          void createInvoiceForTimeClient(targetClientId)
                        }
                      >
                        <ButtonText>
                          {invoices.create.isPending
                            ? "Creating..."
                            : "Create draft"}
                        </ButtonText>
                      </Button>
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
            {selectedInvoice.client_id ? (
              <AppText size="xs" tone="muted">
                Showing time linked to {selectedClient?.name ?? "this client"}.
                {otherClientTimeEntryCount > 0
                  ? ` ${otherClientTimeEntryCount} other billable entr${
                      otherClientTimeEntryCount === 1 ? "y is" : "ies are"
                    } hidden.`
                  : ""}
              </AppText>
            ) : null}
            {invoiceReadyTimeEntries.slice(0, 8).map((entry) => {
              const hasRate = Boolean(entry.hourly_rate_cents);
              const matchingClientId = entryClientId(entry);
              const entryClient = matchingClientId
                ? clientById.get(matchingClientId)
                : null;
              const project = entry.project_id
                ? projectById.get(entry.project_id)
                : null;
              const task = entry.task_id ? taskById.get(entry.task_id) : null;
              return (
                <View key={entry.id} style={stylesheet.itemRow}>
                  <View style={stylesheet.itemMain}>
                    <AppText weight="semibold">
                      {entry.description ?? "Billable time"}
                    </AppText>
                    <AppText size="xs" tone="muted">
                      {formatDuration(entry.duration_seconds)} -{" "}
                      {hasRate
                        ? formatMoney(timeEntryValue(entry))
                        : "No rate set"}
                    </AppText>
                    <AppText size="xs" tone="muted">
                      {entryClient?.name ?? "No client"} -{" "}
                      {project?.name ?? "No project"} -{" "}
                      {task?.name ?? "No task"}
                    </AppText>
                  </View>
                  <Button
                    compact
                    variant="outline"
                    disabled={
                      !hasRate || invoiceItems.createFromTimeEntry.isPending
                    }
                    onPress={() => void addTimeEntry(entry)}
                  >
                    <ButtonText>
                      {invoiceItems.createFromTimeEntry.isPending
                        ? "Adding..."
                        : "Add to invoice"}
                    </ButtonText>
                  </Button>
                </View>
              );
            })}
          </Stack>
        )}
      </Card>

      <Card gap="md">
        <View style={stylesheet.listHeader}>
          <Stack gap="xs">
            <AppText weight="semibold">Client expenses ready to rebill</AppText>
            <AppText size="xs" tone="muted">
              Add pass-through costs already linked to this invoice client.
            </AppText>
          </Stack>
        </View>
        {!selectedInvoice?.client_id ? (
          <View style={stylesheet.emptyState}>
            <AppText size="sm">Select a client invoice first.</AppText>
          </View>
        ) : invoiceReadyExpenses.length === 0 ? (
          <View style={stylesheet.emptyState}>
            <AppText size="sm">
              No rebillable expenses are ready for this client.
            </AppText>
          </View>
        ) : (
          <Stack gap="sm">
            {invoiceReadyExpenses.slice(0, 8).map((expense) => (
              <View key={expense.id} style={stylesheet.itemRow}>
                <View style={stylesheet.itemMain}>
                  <AppText weight="semibold">{expense.supplier}</AppText>
                  <AppText size="xs" tone="muted">
                    {expense.description} - {expense.expense_date} -{" "}
                    {formatMoney(expense.total_cents)}
                  </AppText>
                </View>
                <Button
                  compact
                  variant="outline"
                  disabled={invoiceItems.createFromExpense.isPending}
                  onPress={() => void addExpense(expense)}
                >
                  <ButtonText>
                    {invoiceItems.createFromExpense.isPending
                      ? "Adding..."
                      : "Add to invoice"}
                  </ButtonText>
                </Button>
              </View>
            ))}
          </Stack>
        )}
      </Card>
    </ProtectedScreen>
  );
}
